import * as Redux from '@reduxjs/toolkit';
import * as lodash from 'lodash';
import type { UseFormMethods } from 'react-hook-form';
import { push } from 'react-router-redux';
import { LocalStorage } from '~/helpers';
import { toastr } from '~/helpers/Toastr';
import type { SecondarySequence } from '~/ogm-hub';
import {
	fetchDistributionCentersForDssiCodes,
	fetchDssiCodesForCustomerAndSupplier,
} from '~/Slices/DistributionCentersSlice';
import {
	createInvoiceSequence,
	getInvoiceSequenceById,
	updateAssociatedDistrubitonCenters,
} from '~/Slices/InvoiceSequenceSlice';
import { LoadingStatus } from '~/Slices/schema';
import { createDispatchProps, createThunk } from '../../async-actions';

export interface NewOrEditSequencePageState {
	action: 'new' | 'edit';
	sequenceId?: number;
	chartId: string;
	data: SequenceOpCo[];
	loadingStatus: LoadingStatus;
	confirmationModalState: {
		showConfirmationModal: boolean;
		added: SequenceOpCo[];
		removed: SequenceOpCo[];
	};
	initialSelections: number[];
	finalSelections: string[];
}

export interface NewOrEditSequenceFormFields {
	chartId?: string;
	selectedSecondarySequence?: SecondarySequence;
	selectedOpCoIds: string[];
}

export interface SequenceOpCo {
	id: number;
	code: string;
	name: string;
}

export interface NewOrEditSequencePageArgs {
	sequenceId: number;
}

export function makeInitialState(): NewOrEditSequencePageState {
	return {
		action: 'edit',
		sequenceId: null,
		chartId: '',
		data: [],
		loadingStatus: LoadingStatus.loading,
		confirmationModalState: {
			showConfirmationModal: false,
			added: [],
			removed: [],
		},
		finalSelections: [],
		initialSelections: [],
	};
}

export const slice = Redux.createSlice({
	name: 'edit-seq',
	initialState: makeInitialState(),
	reducers: {
		pageLoadedPending(
			state,
			action: Redux.PayloadAction<NewOrEditSequencePageArgs>
		) {
			state.loadingStatus = LoadingStatus.loading;
			state.sequenceId = action.payload.sequenceId;
			if (action.payload.sequenceId == null) {
				state.action = 'new';
			}
		},
		pageLoadedFailure(state) {
			state.loadingStatus = LoadingStatus.failure;
		},
		loadedSelectedOpcos(state, action: Redux.PayloadAction<number[]>) {
			state.initialSelections = action.payload;
		},
		loadedOpcosForCustomer(state, action: Redux.PayloadAction<any[]>) {
			state.loadingStatus = LoadingStatus.success;
			state.data = action.payload;
		},
		pageUnloaded() {
			return makeInitialState();
		},

		sequenceDraftDeletedSuccess(state) {
			state.loadingStatus = LoadingStatus.success;
		},
		confirmChanges(state, action: any) {
			state.confirmationModalState = {
				showConfirmationModal: true,
				added: action.payload.additions,
				removed: action.payload.removes,
			};
			state.finalSelections = action.payload.finalSelections;
		},
		savingChanges(state) {
			state.loadingStatus = LoadingStatus.loading;
		},
		cancelSave(state) {
			state.confirmationModalState = {
				showConfirmationModal: false,
				added: [],
				removed: [],
			};
			state.finalSelections = [];
		},
	},
	extraReducers: (builder) => {
		builder.addCase(createInvoiceSequence.pending, (state) => {
			state.loadingStatus = LoadingStatus.loading;
		});
		builder.addCase(createInvoiceSequence.fulfilled, (state) => {
			state.loadingStatus = LoadingStatus.success;
		});
	},
});

export interface PageLoadedArgs {
	params: NewOrEditSequencePageArgs;
	form: UseFormMethods<NewOrEditSequenceFormFields>;
}

const pageLoaded = createThunk<PageLoadedArgs>(async (args, dispatch) => {
	dispatch(slice.actions.pageLoadedPending(args.params));
	const action = args.params.sequenceId == null ? 'new' : 'edit';
	let distributionCentersSelected = [];

	if (action === 'edit') {
		const customerId = LocalStorage.getCustomerId();
		const headerResponse = await dispatch(
			getInvoiceSequenceById({
				sequenceId: args.params.sequenceId,
				customerId,
			})
		);

		if (getInvoiceSequenceById.fulfilled.match(headerResponse)) {
			dispatch(
				slice.actions.loadedSelectedOpcos(
					headerResponse.payload.distributionCenterIds
				)
			);
			distributionCentersSelected =
				headerResponse.payload.distributionCenterIds;
		} else {
			dispatch(slice.actions.pageLoadedFailure());
			return;
		}
	}

	const dcCodesResponse = await dispatch(
		fetchDssiCodesForCustomerAndSupplier({
			customerId: LocalStorage.getCustomerId(),
			supplierId: 1,
		})
	);

	if (fetchDssiCodesForCustomerAndSupplier.fulfilled.match(dcCodesResponse)) {
		const dcResponse = await dispatch(
			fetchDistributionCentersForDssiCodes({
				distributionCenterDssiCodes: dcCodesResponse.payload,
			})
		);

		if (fetchDistributionCentersForDssiCodes.fulfilled.match(dcResponse)) {
			dispatch(
				slice.actions.loadedOpcosForCustomer(
					dcResponse.payload
						.map((dc) => ({
							id: dc.id,
							code: dc.externalCode,
							name: dc.name,
							isAlreadyAssigned: distributionCentersSelected.includes(dc.id),
						}))
						.sort((a, b) => (a.name > b.name ? 1 : -1))
				)
			);
			return;
		}
	}
	return;
});

export interface SequenceSavedArgs {
	values: NewOrEditSequenceFormFields;
	form: UseFormMethods<NewOrEditSequenceFormFields>;
}

const sequenceSaveClicked = createThunk<SequenceSavedArgs>(
	async (args, dispatch, getState) => {
		const { initialSelections, data, action } = getState().SeqChartEditSlice;

		const selected = args.form.getValues().selectedOpCoIds as any[];

		if (action === 'new') {
			dispatch(createFormSubmitted(args));
		} else {
			const additions = selected.filter((x) => !initialSelections.includes(x));
			const removes = initialSelections.filter((x) => !selected.includes(x));

			if (additions.length > 0 || removes.length > 0) {
				dispatch(
					slice.actions.confirmChanges({
						additions: lodash.filter(data, (x) => additions.includes(x.id)),
						removes: lodash.filter(data, (x) => removes.includes(x.id)),
						finalSelections: selected,
					})
				);
			} else {
				toastr.success({
					title: 'Update Successful',
					body: 'Invoice sequence was successully updated',
				});
				dispatch(leavePage());
			}
		}
	}
);

const saveConfirmed = createThunk<void>(async (args, dispatch, getState) => {
	dispatch(slice.actions.savingChanges());

	const { sequenceId, finalSelections } = getState().SeqChartEditSlice;

	const response = await dispatch(
		updateAssociatedDistrubitonCenters({
			invoiceSequenceId: sequenceId,
			args: {
				distributionCenterIds: finalSelections.map((x) => parseInt(x, 10)),
			},
		})
	);

	if (updateAssociatedDistrubitonCenters.fulfilled.match(response)) {
		toastr.success({
			title: 'Update Successful',
			body: 'Invoice sequence was successully updated',
		});
		dispatch(leavePage());
	}
});

export interface CreateFormSubmittedArgs {
	values: NewOrEditSequenceFormFields;
	form: UseFormMethods<NewOrEditSequenceFormFields>;
}
const createFormSubmitted = createThunk<CreateFormSubmittedArgs>(
	async (args, dispatch) => {
		let successfulSubmission = false;
		const customerId = LocalStorage.getCustomerId();
		const params = {
			customerId,
			chartName: args.values.chartId,
			secondarySequence: args.values.selectedSecondarySequence,
		};
		const invoiceSequenceResponse = await dispatch(
			createInvoiceSequence(params)
		);

		if (
			createInvoiceSequence.fulfilled.match(invoiceSequenceResponse) &&
			args.values.selectedOpCoIds.length > 0
		) {
			const associationsResponse = await dispatch(
				updateAssociatedDistrubitonCenters({
					invoiceSequenceId: invoiceSequenceResponse.payload.id,
					args: {
						distributionCenterIds: args.values.selectedOpCoIds.map((x) =>
							parseInt(x, 10)
						),
					},
				})
			);

			if (
				updateAssociatedDistrubitonCenters.fulfilled.match(associationsResponse)
			) {
				successfulSubmission = true;
			} else {
				const errorString = (associationsResponse.payload as string[]).join(
					', '
				);
				toastr.error({ title: 'error', body: errorString });
			}
		} else if (createInvoiceSequence.fulfilled.match(invoiceSequenceResponse)) {
			successfulSubmission = true;
		} else {
			const errorString = (invoiceSequenceResponse.payload as string[]).join(
				', '
			);
			toastr.error({ title: 'error', body: errorString });
		}

		if (successfulSubmission) {
			toastr.success({
				title: 'Successful',
				body: 'Invoice sequence was successully created',
			});
			dispatch(leavePage());
			return;
		}
	}
);

const leavePage = createThunk(async (_, dispatch) => {
	dispatch(push('/invoiceSequences'));
});

const asyncActions = {
	pageLoaded,
	sequenceSaveClicked,
	saveConfirmed,
	leavePage,
};

export const dispatchProps = createDispatchProps(slice.actions, asyncActions);
